/**
 * Data-flow classification (Q1 / Q2 inputs) — V105-3-5 / ADR-0072 §2, §5, D-105-9.
 *
 *   Q1  Does the item handle federal customer data (FRD-FCD)?        yes | metadata_only | no
 *   Q2  Asked only when Q1 = no: could it affect that data's CIA (MAS-CSO-IIR)?   yes | no
 *
 * Answers live in `infra_verdict_answers` (migration 460, ADR-0072 §2 shape): typed, append-only,
 * provenance on every row. Two ways in, both ending with a PERSON:
 *   - a person answers (a `confirmed`, `attested` row);
 *   - agent A10 SUGGESTS through the ADR-0073 egress gateway (task `infra.verdict_suggest`, the
 *     item's facts as the tenant-class field `infra.verdict_inputs`), citing the facts it was
 *     given and the rule ids it applied, or abstaining. Its output is a `suggested` row and never
 *     counts as an answer; a person confirms it (ADR-0039: agents propose, deterministic code
 *     decides). A suggestion that cites nothing it was given is discarded (cite or abstain).
 * Confirming Q1 / Q2 writes the item's boundary components' `federal_data` in the same batch
 * (ADR-0072 §5): yes → handles; metadata_only → impacts + holds_metadata; Q1 no + Q2 yes →
 * impacts; Q1 no + Q2 no → neither.
 *
 * Drafts (ADR-0072 §5), from CONFIRMED answers only, listing what is still unanswered:
 *   MAS-CSO-IIR  `gacpro/fedramp-mas-iir-draft@1`: in-scope resources + the services outside
 *                the minimum assessment scope (the MAS-CSO-SUP candidate set)
 *   MAS-CSO-FLO  `gacpro/fedramp-mas-flo@1`: one flow per in-scope item (its interconnections
 *                when recorded, else offering → item), data class, both ends' security category
 *                and federal data; `validateMasFloDraft` checks the shape.
 */
import { defineTemplate, field } from '../egress/data-classes.js';
import { LlmRefusedError } from '../egress/gateway.js';
import { canonicalJson, generateRandomId, sha256Hex } from '../utils/audit-crypto.js';
import { runAgent } from './agent-harness.js';
import { getInventoryItem, InfraInventoryError, infraFunctionLabel, listInventoryItems } from './infra-inventory-service.js';
import { getIntakeQuestion } from './infra-intake-service.js';
export const Q1_VALUES = ['yes', 'metadata_only', 'no'];
export const Q2_VALUES = ['yes', 'no'];
/** Rule ids a suggestion may cite (CR26 rules dataset 2026.10.05.01). */
export const INFRA_RULE_CITATIONS = ['FRD-FCD', 'MAS-CSO-IIR', 'MAS-CSO-MDI'];
export const A10_DEFINITION = {
    agent_id: 'A10',
    prompt_version: '2026.10.08',
    queue_target: 'infra_verdict_answers',
    schema_version: '1.0',
};
const SUGGEST_PROMPT = [
    'You classify one third-party service for a FedRAMP 20x inventory. You only SUGGEST; a person decides.',
    '',
    'Rules (CR26 2026.10.05.01):',
    '- FRD-FCD: federal customer data is what an agency or its authorized users upload, store or otherwise supply. It does NOT include account information, service metadata, analytics, telemetry or similar metadata generated by the provider.',
    '- MAS-CSO-MDI: metadata, including metadata about federal customer data, is in the minimum assessment scope.',
    '- MAS-CSO-IIR: the offering includes every resource likely to handle federal customer data or likely to impact its confidentiality, integrity or availability.',
    '',
    'Questions:',
    '- q1: does this service handle federal customer data? "yes", "metadata_only" (holds only account information, service metadata or metadata about that data) or "no".',
    '- q2 (only when q1 is "no"): could this service affect the confidentiality, integrity or availability of federal customer data (for example identity, CI/CD, DNS, edge)? "yes" or "no".',
    '',
    'Facts about the service (each has an id):',
    '{item_facts}',
    '',
    'Answer only from these facts and rules. Cite the fact ids and rule ids you used. If the facts do not support an answer, abstain.',
    'Return JSON: {"q1": {"value": "...", "citations": ["F1", "FRD-FCD"], "rationale": "one sentence"}, "q2": {"value": "...", "citations": [...], "rationale": "..."} or null, "abstain": false}',
].join('\n');
export const INFRA_SUGGEST_TEMPLATE = defineTemplate('infra.verdict_suggest', '2026.10.08', SUGGEST_PROMPT, ['item_facts']);
const SUGGEST_SCHEMA = {
    type: 'object',
    properties: { q1: { type: 'object' }, q2: { type: 'object' }, abstain: { type: 'boolean' } },
};
/** The item's facts, numbered. Tenant data (paths, bindings, component names): class `infra.verdict_inputs`. */
export function buildItemFacts(item, components) {
    const facts = [];
    facts.push(`Service: ${item.name} (${item.ref}).`);
    facts.push(`Function: ${infraFunctionLabel(item.function ?? item.suggested_function)}${item.function ? '' : ' (suggested, not confirmed)'}.`);
    for (const s of item.sources.filter((x) => !x.gone_at)) {
        const d = s.detail;
        if (s.source_kind === 'repo_scan') {
            const hits = (Array.isArray(d.hits) ? d.hits : []);
            for (const h of hits.slice(0, 6))
                facts.push(`Found in code: ${String(h.kind)} ${String(h.value)} at ${String(h.path)}:${String(h.line)}.`);
        }
        else if (s.source_kind === 'connector_resource') {
            facts.push(`A connected cloud lists ${String(d.resource_count ?? '?')} resource(s) of type ${(Array.isArray(d.resource_types) ? d.resource_types : []).join(', ')}.`);
        }
        else if (s.source_kind === 'sbom_component') {
            facts.push(`An AI system's SBOM includes ${(Array.isArray(d.components) ? d.components : []).slice(0, 5).join(', ')}.`);
        }
        else if (s.source_kind === 'oauth_app') {
            facts.push(`Staff sign in to ${String(d.name ?? 'this app')} through the identity provider (${String(d.user_count ?? '?')} users).`);
        }
        else if (s.source_kind === 'egress') {
            facts.push(`Outbound traffic was seen to ${String(d.domain)} (${String(d.hits ?? '?')} requests).`);
        }
        else if (s.source_kind === 'intake') {
            const q = getIntakeQuestion(String(d.question_key ?? ''));
            facts.push(`A person answered "${q?.text ?? String(d.question_key)}" with this service.`);
        }
    }
    for (const c of components) {
        facts.push(`Boundary component ${c.component_key}: federal data ${c.federal_data ?? 'not answered'}${c.holds_metadata ? ', holds metadata' : ''}${c.security_category ? `, security category ${c.security_category}` : ''}.`);
    }
    return [...new Set(facts)].slice(0, 40).map((text, i) => ({ id: `F${i + 1}`, text }));
}
/**
 * Cite or abstain. Keeps a suggestion only when its value is in the vocabulary and it cites at
 * least one given fact and one rule id (unknown citations are dropped, never invented). q2 is kept
 * only when q1 = no. Returns null to abstain.
 */
export function validateSuggestion(raw, factIds) {
    if (!raw || typeof raw !== 'object')
        return null;
    const o = raw;
    if (o.abstain === true)
        return null;
    const one = (x, vocab) => {
        if (!x || typeof x !== 'object')
            return null;
        const r = x;
        if (typeof r.value !== 'string' || !vocab.includes(r.value))
            return null;
        const cites = [...new Set((Array.isArray(r.citations) ? r.citations : []).map((c) => String(c).trim().toUpperCase()))];
        const facts = cites.filter((c) => factIds.has(c));
        const rules = cites.filter((c) => INFRA_RULE_CITATIONS.includes(c));
        if (!facts.length || !rules.length)
            return null;
        const rationale = typeof r.rationale === 'string' ? r.rationale.trim().slice(0, 400) : '';
        return { value: r.value, citations: [...facts, ...rules], rationale };
    };
    const q1 = one(o.q1, Q1_VALUES);
    if (!q1)
        return null;
    const q2 = q1.value === 'no' ? one(o.q2, Q2_VALUES) : null;
    return { q1, q2 };
}
const parse = (j, d) => { try {
    return JSON.parse(j);
}
catch {
    return d;
} };
const toAnswer = (r) => {
    const { value_json, source_ref_json, citations_json, ...rest } = r;
    return { ...rest, value: parse(value_json, null), source_ref: parse(source_ref_json, {}), citations: parse(citations_json, []) };
};
export async function listItemAnswers(db, workspaceId, itemId, opts = {}) {
    return (await db.query(`SELECT * FROM infra_verdict_answers WHERE workspace_id = ? AND item_id = ? ${opts.includeSuperseded ? '' : "AND status <> 'superseded'"}
      ORDER BY question ASC, CASE status WHEN 'confirmed' THEN 0 WHEN 'suggested' THEN 1 ELSE 2 END, created_at DESC, answer_id ASC`, [workspaceId, itemId])).results.map(toAnswer);
}
/** Confirmed Q1 / Q2 per item (the only values that count). */
export async function confirmedClassifications(db, workspaceId) {
    const rows = (await db.query(`SELECT * FROM infra_verdict_answers WHERE workspace_id = ? AND status = 'confirmed' AND question IN ('q1', 'q2')`, [workspaceId])).results.map(toAnswer);
    const out = new Map();
    for (const a of rows) {
        const e = out.get(a.item_id) ?? {};
        if (a.question === 'q1')
            e.q1 = a;
        else if (a.question === 'q2')
            e.q2 = a;
        out.set(a.item_id, e);
    }
    return out;
}
function assertValue(question, value) {
    const vocab = question === 'q1' ? Q1_VALUES : Q2_VALUES;
    if (typeof value !== 'string' || !vocab.includes(value))
        throw new InfraInventoryError('invalid', `${question} must be one of ${vocab.join(', ')}.`);
}
/** federal_data on the item's components from its confirmed Q1 / Q2 (ADR-0072 §5); undetermined → unchanged. */
async function writeComponentFederalData(db, workspaceId, itemId, nowIso) {
    const c = (await confirmedClassifications(db, workspaceId)).get(itemId) ?? {};
    const q1 = c.q1?.value;
    const q2 = c.q2?.value;
    let fd = null;
    let meta = null;
    if (q1 === 'yes') {
        fd = 'handles';
    }
    else if (q1 === 'metadata_only') {
        fd = 'impacts';
        meta = 1;
    }
    else if (q1 === 'no' && q2) {
        fd = q2 === 'yes' ? 'impacts' : 'neither';
        meta = 0;
    }
    if (!fd)
        return;
    await db.execute(`UPDATE boundary_components SET federal_data = ?, holds_metadata = COALESCE(?, holds_metadata), updated_at = ? WHERE workspace_id = ? AND infra_item_id = ? AND removed_at IS NULL`, [fd, meta, nowIso, workspaceId, itemId]);
}
async function itemOrNotFound(db, workspaceId, itemId) {
    const item = await getInventoryItem(db, workspaceId, itemId);
    if (!item)
        throw new InfraInventoryError('not_found', 'Inventory item not found.');
    return item;
}
/** A person answers Q1 or Q2 directly: a confirmed, attested row (the prior confirmed row and open suggestions are superseded). */
export async function answerItemQuestion(db, input) {
    await itemOrNotFound(db, input.workspace_id, input.item_id);
    assertValue(input.question, input.value);
    if (!input.actor_user_id || input.actor_user_id.startsWith('system:'))
        throw new InfraInventoryError('invalid', 'Q1 and Q2 are answered by a person.');
    const nowIso = (input.now ?? new Date()).toISOString();
    const id = (input.newId ?? ((p) => generateRandomId(p)))('iva');
    await db.batch([
        { sql: `UPDATE infra_verdict_answers SET status = 'superseded' WHERE workspace_id = ? AND item_id = ? AND question = ? AND status IN ('confirmed', 'suggested')`, params: [input.workspace_id, input.item_id, input.question] },
        {
            sql: `INSERT INTO infra_verdict_answers (answer_id, workspace_id, item_id, question, value_json, provenance, source_ref_json, citations_json, status, confirmed_by, confirmed_at, created_at)
            VALUES (?, ?, ?, ?, ?, 'attested', ?, '[]', 'confirmed', ?, ?, ?)`,
            params: [id, input.workspace_id, input.item_id, input.question, JSON.stringify(input.value), JSON.stringify({ answered_by: input.actor_user_id }), input.actor_user_id, nowIso, nowIso],
        },
    ]);
    await writeComponentFederalData(db, input.workspace_id, input.item_id, nowIso);
    return listItemAnswers(db, input.workspace_id, input.item_id);
}
/** A person confirms a suggestion (it becomes the answer; the prior confirmed row is superseded in the same batch). */
export async function confirmSuggestedAnswer(db, input) {
    const row = (await db.query(`SELECT * FROM infra_verdict_answers WHERE answer_id = ? AND workspace_id = ? LIMIT 1`, [input.answer_id, input.workspace_id])).results[0];
    if (!row)
        throw new InfraInventoryError('not_found', 'Answer not found.');
    if (row.status !== 'suggested')
        throw new InfraInventoryError('conflict', `This answer is already ${row.status}.`);
    if (!input.actor_user_id || input.actor_user_id.startsWith('system:'))
        throw new InfraInventoryError('invalid', 'A suggestion is confirmed by a person.');
    const nowIso = (input.now ?? new Date()).toISOString();
    await db.batch([
        { sql: `UPDATE infra_verdict_answers SET status = 'superseded' WHERE workspace_id = ? AND item_id = ? AND question = ? AND status = 'confirmed'`, params: [input.workspace_id, row.item_id, row.question] },
        { sql: `UPDATE infra_verdict_answers SET status = 'confirmed', confirmed_by = ?, confirmed_at = ? WHERE answer_id = ? AND workspace_id = ? AND status = 'suggested'`, params: [input.actor_user_id, nowIso, row.answer_id, input.workspace_id] },
    ]);
    await writeComponentFederalData(db, input.workspace_id, row.item_id, nowIso);
    return listItemAnswers(db, input.workspace_id, row.item_id);
}
/**
 * Ask agent A10 for Q1 (and Q2 when Q1 = no) through the egress gateway. Writes `suggested`
 * rows only (prior open suggestions superseded); a person confirms. A refusal (no provider
 * may receive this workspace's tenant data, e.g. a federal workspace without an authorized
 * provider) is returned for the route's "answer it" action.
 */
export async function suggestItemClassification(db, gateway, input) {
    const item = await itemOrNotFound(db, input.workspace_id, input.item_id);
    const comps = (await db.query(`SELECT component_id, component_key, federal_data, holds_metadata, security_category FROM boundary_components WHERE workspace_id = ? AND infra_item_id = ? AND removed_at IS NULL ORDER BY component_key`, [input.workspace_id, item.item_id])).results;
    const facts = buildItemFacts(item, comps);
    const factIds = new Set(facts.map((f) => f.id));
    const factText = facts.map((f) => `${f.id}: ${f.text}`).join('\n');
    const factsSha = await sha256Hex(factText);
    let validated = null;
    const agentFn = async (llm) => {
        const r = await llm.run({
            task: 'infra.verdict_suggest', template: INFRA_SUGGEST_TEMPLATE,
            slots: { item_facts: field('infra.verdict_inputs', factText) }, schemaHint: SUGGEST_SCHEMA, options: { temperature: 0 },
        });
        if (r.status !== 'ok')
            throw new LlmRefusedError(r);
        validated = validateSuggestion(r.output, factIds);
        if (!validated)
            throw new Error('A10 abstained: no answer cited the facts and rules it was given.');
        const v = validated;
        const proposal = { item_id: item.item_id, q1: v.q1, ...(v.q2 ? { q2: v.q2 } : {}) };
        return {
            target: A10_DEFINITION.queue_target,
            proposal,
            provenance: {
                item_id: { source: `inventory:${item.ref}` },
                q1: { source: v.q1.citations.join(','), span: v.q1.rationale, confidence: r.confidence },
                ...(v.q2 ? { q2: { source: v.q2.citations.join(','), span: v.q2.rationale, confidence: r.confidence } } : {}),
            },
        };
    };
    let runId;
    try {
        runId = (await runAgent(db, gateway, A10_DEFINITION, { workspace_id: input.workspace_id, data: { item: item.ref, facts_sha256: factsSha } }, agentFn, { requireProvenance: true, provenanceKeys: ['q1'] })).run_id;
    }
    catch (err) {
        if (err instanceof LlmRefusedError)
            return { status: 'refused', refusal: err.refusal };
        if (err instanceof Error && err.message.startsWith('A10 abstained')) {
            const last = (await db.query(`SELECT run_id FROM agent_runs WHERE workspace_id = ? AND agent_id = 'A10' ORDER BY started_at DESC LIMIT 1`, [input.workspace_id])).results[0];
            return { status: 'abstained', run_id: last?.run_id ?? null, reason: 'The suggestion did not cite the facts and rules it was given, so it was discarded. Answer it directly.' };
        }
        throw err;
    }
    const v = validated;
    if (!v)
        return { status: 'abstained', run_id: runId, reason: 'No suggestion.' };
    const nowIso = (input.now ?? new Date()).toISOString();
    const newId = input.newId ?? ((p) => generateRandomId(p));
    const textOf = new Map(facts.map((f) => [f.id, f.text]));
    const rows = ['q1', 'q2'].flatMap((q) => {
        const s = v[q];
        if (!s)
            return [];
        return [{
                sql: `INSERT INTO infra_verdict_answers (answer_id, workspace_id, item_id, question, value_json, provenance, source_ref_json, citations_json, suggested_by_run_id, status, created_at)
            VALUES (?, ?, ?, ?, ?, 'attested', ?, ?, ?, 'suggested', ?)`,
                params: [newId('iva'), input.workspace_id, item.item_id, q, JSON.stringify(s.value),
                    JSON.stringify({ agent_id: A10_DEFINITION.agent_id, prompt_version: A10_DEFINITION.prompt_version, facts_sha256: factsSha, rationale: s.rationale }),
                    JSON.stringify(s.citations.map((ref) => (textOf.has(ref) ? { ref, text: textOf.get(ref) } : { ref }))), runId, nowIso],
            }];
    });
    await db.batch([
        { sql: `UPDATE infra_verdict_answers SET status = 'superseded' WHERE workspace_id = ? AND item_id = ? AND question IN ('q1', 'q2') AND status = 'suggested'`, params: [input.workspace_id, item.item_id] },
        ...rows,
    ]);
    return { status: 'suggested', run_id: runId, answers: await listItemAnswers(db, input.workspace_id, item.item_id) };
}
/** ADR-0072 §2 / PRD §5.2: Q2's deterministic default (a suggestion shown to the person, never an answer). */
export function defaultQ2(fn) {
    return fn === 'identity' || fn === 'ci_cd' || fn === 'edge_network' ? 'yes' : 'no';
}
// ---- MAS drafts ---------------------------------------------------------------------------------------------
export const MAS_FLO_FORMAT = 'gacpro/fedramp-mas-flo@1';
export const MAS_IIR_DRAFT_FORMAT = 'gacpro/fedramp-mas-iir-draft@1';
export const FLO_DATA_CLASSES = ['federal_customer_data', 'metadata', 'no_federal_data'];
async function draftInputs(db, workspaceId) {
    const items = (await listInventoryItems(db, workspaceId)).filter((i) => i.status === 'accepted' || i.status === 'linked');
    const components = (await db.query(`SELECT component_id, component_key, federal_data, holds_metadata, security_category, infra_item_id FROM boundary_components WHERE workspace_id = ? AND infra_item_id IS NOT NULL AND removed_at IS NULL`, [workspaceId])).results;
    const interconnections = (await db.query(`SELECT interconnection_id, component_id, external_service_id, direction, security_category FROM boundary_interconnections WHERE workspace_id = ? AND removed_at IS NULL AND external_service_id IS NOT NULL`, [workspaceId])).results;
    return { items, confirmed: await confirmedClassifications(db, workspaceId), components, interconnections };
}
const sorted = (xs) => xs.sort((a, b) => (a.ref < b.ref ? -1 : a.ref > b.ref ? 1 : 0));
/** Pure: MAS-CSO-IIR draft from accepted / linked items and their CONFIRMED Q1 / Q2. */
export async function renderInfraIirDraft(inp) {
    const resources = [];
    const outside = [];
    const unanswered = [];
    for (const it of inp.items) {
        const c = inp.confirmed.get(it.item_id) ?? {};
        const q1 = c.q1?.value;
        const q2 = c.q2?.value;
        if (!q1) {
            unanswered.push({ ref: it.ref, name: it.name, missing: ['q1'] });
            continue;
        }
        if (q1 === 'no' && !q2) {
            unanswered.push({ ref: it.ref, name: it.name, missing: ['q2'] });
            continue;
        }
        if (q1 === 'no' && q2 === 'no') {
            outside.push({ ref: it.ref, name: it.name, reason: 'OOS_NO_FCD_NO_CIA', basis: { q1: 'no', q2: 'no' } });
            continue;
        }
        resources.push({ ref: it.ref, name: it.name, function: it.function, federal_data: q1 === 'yes' ? 'handles' : 'impacts', holds_metadata: q1 === 'metadata_only', basis: { q1, q2: q1 === 'no' ? q2 ?? null : null } });
    }
    sorted(resources);
    sorted(outside);
    sorted(unanswered);
    return {
        format: MAS_IIR_DRAFT_FORMAT, rule: 'MAS-CSO-IIR', draft: true, information_resources: resources, services_outside_minimum_assessment_scope: outside, unanswered,
        rows_sha256: await sha256Hex(canonicalJson({ resources, outside, unanswered })),
    };
}
/** Pure: MAS-CSO-FLO draft — one flow per in-scope item (its interconnections when recorded). */
export async function renderMasInformationFlows(inp) {
    const flows = [];
    const unanswered = [];
    for (const it of inp.items) {
        const c = inp.confirmed.get(it.item_id) ?? {};
        const q1 = c.q1?.value;
        const q2 = c.q2?.value;
        if (!q1 || (q1 === 'no' && !q2)) {
            unanswered.push({ ref: it.ref, name: it.name, missing: [!q1 ? 'q1' : 'q2'] });
            continue;
        }
        if (q1 === 'no' && q2 === 'no')
            continue; // outside the MAS: no flow to document
        const dataClass = q1 === 'yes' ? 'federal_customer_data' : q1 === 'metadata_only' ? 'metadata' : 'no_federal_data';
        const federal = q1 === 'yes' ? 'handles' : 'impacts';
        const comp = inp.components.find((x) => x.infra_item_id === it.item_id) ?? null;
        const dest = { name: it.name, kind: 'third_party', ref: it.ref, security_category: comp?.security_category ?? null, federal_data: federal };
        const basis = { q1, q2: q1 === 'no' ? q2 ?? null : null, answer_ids: [c.q1.answer_id, ...(c.q2 && q1 === 'no' ? [c.q2.answer_id] : [])] };
        const ics = it.external_service_id ? inp.interconnections.filter((x) => x.external_service_id === it.external_service_id) : [];
        if (ics.length) {
            for (const ic of ics) {
                const src = inp.components.find((x) => x.component_id === ic.component_id);
                flows.push({
                    flow_id: `${it.ref}:${ic.interconnection_id}`,
                    source: { name: src?.component_key ?? ic.component_id, kind: 'component', ref: null, security_category: ic.security_category ?? src?.security_category ?? null, federal_data: src?.federal_data ?? null },
                    destination: dest, direction: ic.direction, data_class: dataClass, basis,
                });
            }
        }
        else {
            flows.push({ flow_id: `${it.ref}:offering`, source: { name: 'The offering', kind: 'offering', ref: null, security_category: null, federal_data: 'handles' }, destination: dest, direction: 'outbound', data_class: dataClass, basis });
        }
    }
    flows.sort((a, b) => (a.flow_id < b.flow_id ? -1 : a.flow_id > b.flow_id ? 1 : 0));
    sorted(unanswered);
    return { format: MAS_FLO_FORMAT, rule: 'MAS-CSO-FLO', draft: true, flows, unanswered, rows_sha256: await sha256Hex(canonicalJson({ flows, unanswered })) };
}
/** Structural validation of a MAS-CSO-FLO draft (`gacpro/fedramp-mas-flo@1`); [] = valid. */
export function validateMasFloDraft(doc) {
    const errs = [];
    if (!doc || typeof doc !== 'object')
        return ['not an object'];
    const d = doc;
    if (d.format !== MAS_FLO_FORMAT)
        errs.push('format');
    if (d.rule !== 'MAS-CSO-FLO')
        errs.push('rule');
    if (typeof d.rows_sha256 !== 'string' || !/^[0-9a-f]{64}$/.test(d.rows_sha256))
        errs.push('rows_sha256');
    if (!Array.isArray(d.flows))
        return [...errs, 'flows'];
    if (!Array.isArray(d.unanswered))
        errs.push('unanswered');
    const ids = new Set();
    d.flows.forEach((f, i) => {
        const x = (f ?? {});
        if (typeof x.flow_id !== 'string' || !x.flow_id)
            errs.push(`flows[${i}].flow_id`);
        else if (ids.has(x.flow_id))
            errs.push(`flows[${i}].flow_id duplicate`);
        else
            ids.add(x.flow_id);
        for (const end of ['source', 'destination']) {
            const e = (x[end] ?? null);
            if (!e || typeof e.name !== 'string' || !e.name) {
                errs.push(`flows[${i}].${end}.name`);
                continue;
            }
            if (!['offering', 'component', 'third_party'].includes(String(e.kind)))
                errs.push(`flows[${i}].${end}.kind`);
            if (e.security_category !== null && !['low', 'moderate', 'high'].includes(String(e.security_category)))
                errs.push(`flows[${i}].${end}.security_category`);
            if (e.federal_data !== null && !['handles', 'impacts', 'neither'].includes(String(e.federal_data)))
                errs.push(`flows[${i}].${end}.federal_data`);
        }
        if (!['inbound', 'outbound', 'bidirectional'].includes(String(x.direction)))
            errs.push(`flows[${i}].direction`);
        if (!FLO_DATA_CLASSES.includes(String(x.data_class)))
            errs.push(`flows[${i}].data_class`);
        const b = (x.basis ?? {});
        if (!Q1_VALUES.includes(String(b.q1)) || !Array.isArray(b.answer_ids) || !b.answer_ids.length)
            errs.push(`flows[${i}].basis`);
    });
    return errs;
}
/** Both drafts for the session workspace. */
export async function renderInfraMasDrafts(db, workspaceId) {
    const inp = await draftInputs(db, workspaceId);
    return { iir: await renderInfraIirDraft(inp), flo: await renderMasInformationFlows(inp) };
}
//# sourceMappingURL=infra-classification-service.js.map